/**
 * Provider Service Preferences Page - KEIKKA Platform
 * Configure service categories, countries served, and notification settings
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Bell, MapPin, Globe, Truck, Settings, Loader2, Check,
  ChevronLeft, Zap, Building2, Phone, Mail, Save,
  CheckCircle2, AlertCircle, Package, Languages
} from 'lucide-react';
import { toast } from 'sonner';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import { ALL_COUNTRIES as GLOBAL_COUNTRIES, getCountryByCode } from '@/lib/countries-data';

const API = process.env.REACT_APP_BACKEND_URL;

// Service categories - using i18n keys
const SERVICE_CATEGORIES = {
  trades: {
    nameKey: "categories.trades",
    items: [
      { key: "electrician", nameKey: "categories.electrician" },
      { key: "plumber", nameKey: "categories.plumber" },
      { key: "welder", nameKey: "categories.welder" },
      { key: "carpenter", nameKey: "categories.carpenter" },
      { key: "painter", nameKey: "categories.painter" },
      { key: "hvac", nameKey: "categories.hvac" },
      { key: "construction", nameKey: "categories.construction" },
      { key: "renovation", nameKey: "categories.renovation" },
      { key: "roofing", nameKey: "categories.roofing" },
      { key: "flooring", nameKey: "categories.flooring" }
    ]
  },
  transport: {
    nameKey: "categories.transport",
    items: [
      { key: "transport", nameKey: "categories.transportation" },
      { key: "moving", nameKey: "categories.moving" },
      { key: "courier", nameKey: "categories.courier" },
      { key: "heavy_transport", nameKey: "categories.heavy_transport" },
      { key: "logistics", nameKey: "categories.logistics" }
    ]
  },
  auto: {
    nameKey: "categories.auto",
    items: [
      { key: "auto_repair", nameKey: "categories.auto_repair" },
      { key: "tire_service", nameKey: "categories.tire_service" },
      { key: "bodywork", nameKey: "categories.bodywork" },
      { key: "diagnostics", nameKey: "categories.diagnostics" }
    ]
  },
  maintenance: {
    nameKey: "categories.maintenance",
    items: [
      { key: "cleaning", nameKey: "categories.cleaning" },
      { key: "property_maintenance", nameKey: "categories.property_maintenance" },
      { key: "landscaping", nameKey: "categories.landscaping" },
      { key: "snow_removal", nameKey: "categories.snow_removal" }
    ]
  },
  tech: {
    nameKey: "categories.tech",
    items: [
      { key: "it_support", nameKey: "categories.it_support" },
      { key: "web_development", nameKey: "categories.web_development" },
      { key: "network_setup", nameKey: "categories.network_setup" }
    ]
  },
  professional: {
    nameKey: "categories.professional",
    items: [
      { key: "consulting", nameKey: "categories.consulting" },
      { key: "legal", nameKey: "categories.legal" },
      { key: "accounting", nameKey: "categories.accounting" },
      { key: "translation", nameKey: "categories.translation" }
    ]
  },
  manufacturing: {
    nameKey: "categories.manufacturing",
    items: [
      { key: "cnc_machining", nameKey: "categories.cnc_machining" },
      { key: "metal_fabrication", nameKey: "categories.metal_fabrication" },
      { key: "manufacturing", nameKey: "categories.manufacturing" }
    ]
  }
};

// Languages - using i18n keys for names
const LANGUAGES = [
  { code: "en", nameKey: "language.names.en" },
  { code: "fi", nameKey: "language.names.fi" },
  { code: "et", nameKey: "language.names.et" },
  { code: "sv", nameKey: "language.names.sv" },
  { code: "no", nameKey: "language.names.no" },
  { code: "de", nameKey: "language.names.de" },
  { code: "pl", nameKey: "language.names.pl" },
  { code: "ru", nameKey: "language.names.ru" },
  { code: "lv", nameKey: "language.names.lv" },
  { code: "lt", nameKey: "language.names.lt" }
];

export function ProviderPreferencesPage() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasPreferences, setHasPreferences] = useState(false);
  
  // Form state
  const [serviceCategories, setServiceCategories] = useState([]);
  const [homeCountry, setHomeCountry] = useState('');
  const [serviceCountries, setServiceCountries] = useState([]);
  const [acceptCrossBorder, setAcceptCrossBorder] = useState(true);
  const [maxDistanceKm, setMaxDistanceKm] = useState(100);
  
  // Transport capabilities
  const [transportCapable, setTransportCapable] = useState(false);
  const [maxCargoWeightKg, setMaxCargoWeightKg] = useState('');
  
  // Notifications
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [instantAlerts, setInstantAlerts] = useState(true);
  const [dailyDigest, setDailyDigest] = useState(false);
  
  // Availability
  const [availableNow, setAvailableNow] = useState(true);
  const [languagesSpoken, setLanguagesSpoken] = useState(['en']);
  const [matchScoreThreshold, setMatchScoreThreshold] = useState(60);
  
  useEffect(() => {
    if (token) {
      fetchPreferences();
    } else {
      setLoading(false);
    }
  }, [token]);
  
  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API}/api/service-requests/provider/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setHasPreferences(true);
          setServiceCategories(data.service_categories || []);
          setHomeCountry(data.home_country);
          setServiceCountries(data.service_countries || []);
          setAcceptCrossBorder(data.accept_cross_border ?? true);
          setMaxDistanceKm(data.max_distance_km || 100);
          setTransportCapable(data.transport_capable || false);
          setMaxCargoWeightKg(data.max_cargo_weight_kg?.toString());
          setEmailNotifications(data.email_notifications ?? true);
          setSmsNotifications(data.sms_notifications || false);
          setInstantAlerts(data.instant_alerts ?? true);
          setDailyDigest(data.daily_digest || false);
          setAvailableNow(data.available_now ?? true);
          setLanguagesSpoken(data.languages_spoken || ['en']);
          setMatchScoreThreshold(data.match_score_threshold || 60);
        }
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (serviceCategories.length === 0) {
      toast.error(t('providerPreferences.errorSelectCategory'));
      return;
    }
    if (!homeCountry) {
      toast.error(t('providerPreferences.errorSelectHomeCountry'));
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        service_categories: serviceCategories,
        home_country: homeCountry,
        service_countries: serviceCountries.length > 0 ? serviceCountries : [homeCountry],
        accept_cross_border: acceptCrossBorder,
        max_distance_km: maxDistanceKm,
        transport_capable: transportCapable,
        transport_vehicle_types: [],
        max_cargo_weight_kg: maxCargoWeightKg ? parseFloat(maxCargoWeightKg) : null,
        max_cargo_volume_m3: null,
        email_notifications: emailNotifications,
        sms_notifications: smsNotifications,
        instant_alerts: instantAlerts,
        daily_digest: dailyDigest,
        available_now: availableNow,
        minimum_project_duration_days: null,
        maximum_project_duration_days: null,
        languages_spoken: languagesSpoken,
        min_budget_threshold: null,
        match_score_threshold: matchScoreThreshold
      };
      
      const method = hasPreferences ? 'PUT' : 'POST';
      const response = await fetch(`${API}/api/service-requests/provider/preferences`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setHasPreferences(true);
        toast.success(t('providerPreferences.savedToast'));
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      toast.error(t('providerPreferences.error'));
    } finally {
      setSaving(false);
    }
  };
  
  const toggleCategory = (key) => {
    setServiceCategories(prev =>
      prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]
    );
  };
  
  const toggleCountry = (code) => {
    setServiceCountries(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };
  
  const toggleLanguage = (code) => {
    setLanguagesSpoken(prev =>
      prev.includes(code) ? prev.filter(l => l !== code) : [...prev, code]
    );
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h1 className="text-xl font-semibold mb-2">{t('auth.loginRequired')}</h1>
            <p className="text-gray-600 mb-4">{t('providerPreferences.loginToSetup')}</p>
            <Button onClick={() => navigate('/login')}>{t('auth.login')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('common.back')}
          </Button>
          <h1 className="font-semibold">{t('providerPreferences.title')}</h1>
          <div className="w-16" />
        </div>
      </div>
      
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Status Banner */}
        {hasPreferences ? (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-700">
              <span className="font-medium">{t('providerPreferences.registeredSuccess')}</span> {t('providerPreferences.willReceiveRequests')}
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700">
              {t('providerPreferences.completeProfile')}
            </AlertDescription>
          </Alert>
        )}
        
        {/* Service Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {t('providerPreferences.serviceCategories')}
            </CardTitle>
            <CardDescription>
              {t('providerPreferences.serviceCategoriesDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(SERVICE_CATEGORIES).map(([groupKey, group]) => (
              <div key={groupKey}>
                <h4 className="font-medium text-sm text-gray-700 mb-2">{t(group.nameKey)}</h4>
                <div className="flex flex-wrap gap-2">
                  {group.items.map(item => (
                    <Badge
                      key={item.key}
                      variant={serviceCategories.includes(item.key) ? "default" : "outline"}
                      className="cursor-pointer py-1.5 px-3"
                      onClick={() => toggleCategory(item.key)}
                    >
                      {serviceCategories.includes(item.key) && (
                        <Check className="h-3 w-3 mr-1" />
                      )}
                      {t(item.nameKey)}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
            
            {serviceCategories.length === 0 && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  {t('providerPreferences.selectAtLeastOne')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
        
        {/* Geographic Coverage */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              {t('providerPreferences.geographicCoverage')}
            </CardTitle>
            <CardDescription>
              {t('providerPreferences.locationDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t('providerPreferences.homeCountry')} *</Label>
              <div className="mt-1.5">
                <LocationAutocomplete
                  type="country"
                  value={homeCountry}
                  onChange={setHomeCountry}
                  placeholder={t('location.searchCountry')}
                  allowFreeText={false}
                />
              </div>
            </div>
            
            <div>
              <Label>{t('providerPreferences.countriesYouServe')}</Label>
              <p className="text-xs text-gray-500 mb-2">
                {t('providerPreferences.countriesDescription')}
              </p>
              <div className="flex flex-wrap gap-2">
                {GLOBAL_COUNTRIES.slice(0, 30).map(c => {
                  const key = `location.countries.${c.code}`;
                  const translated = t(key);
                  const countryName = translated !== key ? translated : c.name;
                  return (
                    <Badge
                      key={c.code}
                      variant={serviceCountries.includes(c.code) ? "default" : "outline"}
                      className={`cursor-pointer ${c.code === homeCountry ? 'ring-2 ring-orange-300' : ''}`}
                      onClick={() => toggleCountry(c.code)}
                    >
                      {c.flag} {countryName}
                    </Badge>
                  );
                })}
              </div>
            </div>
            
            <div>
              <Label>{t('providerPreferences.maxRadius', { km: maxDistanceKm })}</Label>
              <Slider
                value={[maxDistanceKm]}
                onValueChange={(v) => setMaxDistanceKm(v[0])}
                min={10}
                max={1000}
                step={10}
                className="mt-2"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>{t('providerPreferences.acceptCrossBorder')}</Label>
                <p className="text-xs text-gray-500">
                  {t('providerPreferences.acceptCrossBorderDescription')}
                </p>
              </div>
              <Switch 
                checked={acceptCrossBorder} 
                onCheckedChange={setAcceptCrossBorder} 
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Transport Capabilities */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              {t('providerPreferences.transportCapabilities')}
            </CardTitle>
            <CardDescription>
              {t('providerPreferences.transportDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>{t('providerPreferences.transportCapable')}</Label>
                <p className="text-xs text-gray-500">
                  {t('providerPreferences.transportCapableDescription')}
                </p>
              </div>
              <Switch 
                checked={transportCapable} 
                onCheckedChange={setTransportCapable} 
              />
            </div>
            
            {transportCapable && (
              <div>
                <Label>{t('providerPreferences.maxCargoWeight')}</Label>
                <Input
                  type="number"
                  placeholder={t('providerPreferences.weightPlaceholder')}
                  value={maxCargoWeightKg}
                  onChange={(e) => setMaxCargoWeightKg(e.target.value)}
                  className="mt-1.5"
                />
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {t('providerPreferences.notifications')}
            </CardTitle>
            <CardDescription>
              {t('providerPreferences.notificationsDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-gray-500" />
                <div>
                  <Label>{t('providerPreferences.emailNotifications')}</Label>
                  <p className="text-xs text-gray-500">{t('providerPreferences.emailNotificationsDescription')}</p>
                </div>
              </div>
              <Switch 
                checked={emailNotifications} 
                onCheckedChange={setEmailNotifications} 
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-gray-500" />
                <div>
                  <Label>{t('providerPreferences.smsNotifications')}</Label>
                  <p className="text-xs text-gray-500">{t('providerPreferences.smsNotificationsDescription')}</p>
                </div>
              </div>
              <Switch 
                checked={smsNotifications} 
                onCheckedChange={setSmsNotifications} 
              />
            </div>
            
            <Separator />
            
            <div className="space-y-3">
              <Label>{t('providerPreferences.notificationFrequency')}</Label>
              <div className="grid grid-cols-2 gap-3">
                <div 
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    instantAlerts ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                  }`}
                  onClick={() => { setInstantAlerts(true); setDailyDigest(false); }}
                >
                  <div className="flex items-center gap-2">
                    <Zap className={`h-4 w-4 ${instantAlerts ? 'text-orange-600' : 'text-gray-400'}`} />
                    <span className="font-medium">{t('providerPreferences.instant')}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('providerPreferences.instantDescription')}
                  </p>
                </div>
                
                <div 
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    dailyDigest ? 'border-orange-500 bg-orange-50' : 'border-gray-200'
                  }`}
                  onClick={() => { setInstantAlerts(false); setDailyDigest(true); }}
                >
                  <div className="flex items-center gap-2">
                    <Package className={`h-4 w-4 ${dailyDigest ? 'text-orange-600' : 'text-gray-400'}`} />
                    <span className="font-medium">{t('providerPreferences.dailyDigest')}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('providerPreferences.dailyDigestDescription')}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Languages & Availability */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5" />
              {t('providerPreferences.languagesAvailability')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t('providerPreferences.languagesSpoken')}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {LANGUAGES.map(lang => (
                  <Badge
                    key={lang.code}
                    variant={languagesSpoken.includes(lang.code) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleLanguage(lang.code)}
                  >
                    {languagesSpoken.includes(lang.code) && (
                      <Check className="h-3 w-3 mr-1" />
                    )}
                    {t(lang.nameKey)}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>{t('providerPreferences.availableNow')}</Label>
                <p className="text-xs text-gray-500">
                  {t('providerPreferences.availableNowDescription')}
                </p>
              </div>
              <Switch 
                checked={availableNow} 
                onCheckedChange={setAvailableNow} 
              />
            </div>
            
            <div>
              <Label>{t('providerPreferences.minMatchScore', { percent: matchScoreThreshold })}</Label>
              <p className="text-xs text-gray-500 mb-2">
                {t('providerPreferences.minMatchScoreDescription')}
              </p>
              <Slider
                value={[matchScoreThreshold]}
                onValueChange={(v) => setMatchScoreThreshold(v[0])}
                min={30}
                max={90}
                step={5}
                className="mt-2"
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Save Button */}
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            {t('common.cancel')}
          </Button>
          <Button
            size="lg"
            onClick={handleSave}
            disabled={saving || serviceCategories.length === 0 || !homeCountry}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('providerPreferences.savePreferences')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ProviderPreferencesPage;
